import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { artworkService, studentService, exhibitionService } from '../../services';
import { LoadingSpinner } from '../ui';

const OverviewContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
  transition: transform ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatNumber = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const StatStatus = styled.div`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  min-height: 48px;
  align-content: center;
`;

const StatError = styled.div`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const RetryButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const QuickActionsSection = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const SectionTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const QuickActionCard = styled(Link)`
  display: block;
  background: ${({ theme }) => theme.colors.gray.light};
  border: 2px solid transparent;
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary}10;
    transform: translateY(-2px);
  }
`;

const ActionIcon = styled.div`
  font-size: 32px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ActionTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ActionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const RecentSection = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const RecentList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
`;

const RecentItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
`;

const RecentItemIcon = styled.div`
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary}20;
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const RecentItemContent = styled.div`
  flex: 1;
`;

const RecentItemTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const RecentItemMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

export function DashboardOverview() {
  // Fetch dashboard statistics
  const artworkStatsQuery = useQuery({
    queryKey: ['admin', 'artwork-stats'],
    queryFn: () => artworkService.getUploadStats(),
  });

  const studentStatsQuery = useQuery({
    queryKey: ['admin', 'student-stats'],
    queryFn: () => studentService.getAllStudentsForAdmin(1, 1),
  });

  const exhibitionStatsQuery = useQuery({
    queryKey: ['admin', 'exhibition-stats'],
    queryFn: () => exhibitionService.getAdminExhibitions(1, 1),
  });

  // Fetch recent activity
  const {
    data: recentArtworks,
    isLoading: loadingArtworks,
    isError: recentArtworksError,
    refetch: refetchRecentArtworks,
  } = useQuery({
    queryKey: ['admin', 'recent-artworks'],
    queryFn: () => artworkService.getAdminArtworks(1, 5),
    select: (data) => data.artworks,
  });

  const quickActions = [
    {
      icon: '🎨',
      title: 'Manage Artworks',
      description: 'View and organize all artworks',
      to: '/admin/artworks'
    },
    {
      icon: '📤',
      title: 'Upload Artwork',
      description: 'Add new student artwork',
      to: '/admin/upload'
    },
    {
      icon: '👥',
      title: 'Add Student',
      description: 'Register a new student',
      to: '/admin/students'
    },
    {
      icon: '🏛️',
      title: 'Create Exhibition',
      description: 'Curate a new exhibition',
      to: '/admin/exhibitions'
    },
    {
      icon: '🏠',
      title: 'View Gallery',
      description: 'See the public gallery',
      to: '/gallery'
    }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <OverviewContainer>
      {/* Statistics Cards */}
      <StatsGrid>
        <StatisticCard
          icon="🎨"
          label="Total Artworks"
          value={artworkStatsQuery.data?.total_artworks}
          isLoading={artworkStatsQuery.isLoading}
          isError={artworkStatsQuery.isError}
          onRetry={() => void artworkStatsQuery.refetch()}
        />
        <StatisticCard
          icon="👥"
          label="Total Students"
          value={studentStatsQuery.data?.total}
          isLoading={studentStatsQuery.isLoading}
          isError={studentStatsQuery.isError}
          onRetry={() => void studentStatsQuery.refetch()}
        />
        <StatisticCard
          icon="🏛️"
          label="Total Exhibitions"
          value={exhibitionStatsQuery.data?.total}
          isLoading={exhibitionStatsQuery.isLoading}
          isError={exhibitionStatsQuery.isError}
          onRetry={() => void exhibitionStatsQuery.refetch()}
        />
      </StatsGrid>

      {/* Quick Actions */}
      <QuickActionsSection>
        <SectionTitle>Quick Actions</SectionTitle>
        <QuickActionsGrid>
          {quickActions.map((action) => (
            <QuickActionCard key={action.to} to={action.to}>
              <ActionIcon>{action.icon}</ActionIcon>
              <ActionTitle>{action.title}</ActionTitle>
              <ActionDescription>{action.description}</ActionDescription>
            </QuickActionCard>
          ))}
        </QuickActionsGrid>
      </QuickActionsSection>

      {/* Recent Activity */}
      <RecentSection>
        <SectionTitle>Recent Artwork</SectionTitle>
        {loadingArtworks ? (
          <StatStatus role="status">
            <LoadingSpinner size="md" text="Loading recent artwork..." />
          </StatStatus>
        ) : recentArtworksError ? (
          <StatError role="alert">
            <span>Unable to load recent artwork.</span>
            <RetryButton onClick={() => void refetchRecentArtworks()}>Retry</RetryButton>
          </StatError>
        ) : recentArtworks && recentArtworks.length > 0 ? (
          <RecentList>
            {recentArtworks.map((artwork) => (
              <RecentItem key={artwork.artwork_id}>
                <RecentItemIcon>🎨</RecentItemIcon>
                <RecentItemContent>
                  <RecentItemTitle>
                    Artwork by {artwork.student_name || 'Unknown Student'}
                  </RecentItemTitle>
                  <RecentItemMeta>
                    Uploaded {formatDate(artwork.uploaded_at)} 
                    {artwork.tags && artwork.tags.length > 0 && 
                      ` • Tags: ${artwork.tags.slice(0, 3).join(', ')}`
                    }
                  </RecentItemMeta>
                </RecentItemContent>
              </RecentItem>
            ))}
          </RecentList>
        ) : (
          <EmptyState>
            No artwork uploaded yet. Start by uploading some student artwork!
          </EmptyState>
        )}
      </RecentSection>
    </OverviewContainer>
  );
}

interface StatisticCardProps {
  icon: string;
  label: string;
  value?: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function StatisticCard({
  icon,
  label,
  value,
  isLoading,
  isError,
  onRetry,
}: StatisticCardProps) {
  const hasValue = typeof value === 'number';

  return (
    <StatCard aria-label={`${label} statistic`}>
      <StatIcon>{icon}</StatIcon>
      {isLoading ? (
        <StatStatus role="status">
          <LoadingSpinner size="sm" text="Loading..." />
        </StatStatus>
      ) : isError || !hasValue ? (
        <StatError role="alert">
          <span>Unable to load statistic.</span>
          <RetryButton onClick={onRetry}>Retry</RetryButton>
        </StatError>
      ) : (
        <StatNumber>{value}</StatNumber>
      )}
      <StatLabel>{label}</StatLabel>
    </StatCard>
  );
}
